import { useState } from "react"
import { useNavigate, useParams } from "react-router"
import { useRestoreContactsMutation } from "../../../../../../features/contactsApi"
import { addBannerWithTimeout } from "../../../../../lib/centralizedBanner/slice"
import { ContactDTO, ContactType, FindAllContactsFilters, FindContactsTargetResponse } from "../../../../../lib/filters/dto"
import { keycloak } from "../../../../../lib/keycloak"
import { useAppDispatch, useAppSelector } from "../../../../../lib/redux/hooks"
import { DownloadIcon } from "../../../../../ui/icons/download"
import { PlusIcon } from "../../../../../ui/icons/plus"
import { TrashIcon } from "../../../../../ui/icons/trash"
import { UserPlusIcon } from "../../../../../ui/icons/userPlus"
import Button from "../../../../../ui/molecules/button"
import ButtonDropdown from "../../../../../ui/molecules/buttonDropdown"
import { Spinner } from "../../../../../ui/molecules/spinner"
import { useDownloadCsv } from "../../../../database/commons/useDownloadCsv"
import { TargetListDTO, TargetListPageEnum } from "../../../dto"
import { setTargetListPage } from "../../../slice"
import { DownloadBulk } from "../../../../database/commons/downloadBulk"
import { PersonDTO } from "../../../../database/people/dto"

interface Props {
    isInBin: boolean
    targetList: TargetListDTO
    selectedIds: { contactId: string, companyId: string }[]
    setSelectedIds: (ids: { contactId: string, companyId: string }[]) => void
    setShowPopup: (show: boolean) => void
    filters: FindAllContactsFilters
    setFilters: (filters: FindAllContactsFilters) => void
    contacts: {
        total: number
        page: number
        perPage: number
        prev: boolean
        next: boolean
        totalPage: number
        data: { company: ContactDTO<ContactType.COMPANY>, employees: PersonDTO[] }[]
    }
    setDeleteBulkContactsModal: (show: boolean) => void
    setShowSoftBulkModal: (show: boolean) => void
}

export function ActionsCompaniesTargetList(props: Props) {
    const projectId = useParams().projectId!

    const { selectedIds, setShowPopup, filters, setFilters, setSelectedIds, contacts, targetList, isInBin, setDeleteBulkContactsModal, setShowSoftBulkModal } = props
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const downloadCompaniesCsv = useDownloadCsv(selectedIds.map(id => id.companyId), () => setSelectedIds([]), ContactType.COMPANY);
    const [showDownloadBulkContactsModal, setShowDownloadBulkContactsModal] = useState(false);


    const [restoreContacts] = useRestoreContactsMutation()
    const handleRestoreContacts = async () => {
        try {
            await restoreContacts({ contacts: selectedIds.map(id => id.contactId), targetList: targetList.id }).unwrap()
            dispatch(addBannerWithTimeout({
                message: 'Contatti ripristinati con successo.',
                variant: 'success'
            }))
        } catch (error) {
            dispatch(addBannerWithTimeout({
                message: 'Si è verificato un errore durante il ripristino dei contatti.',
                variant: 'error'
            }))
        }
    }

    const usersState = useAppSelector(state => state.users)

    return (
        <div className="flex items-center gap-2">
            <DownloadBulk
                contactType={ContactType.COMPANY}
                section="target"
                filters={filters}
                setFilters={setFilters}
                showModal={showDownloadBulkContactsModal}
                setShowModal={setShowDownloadBulkContactsModal}
            />
            {isInBin && selectedIds.length > 0 &&
                !(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) &&
                <Button
                    size={"sm"}
                    label="Ripristina"
                    iconPosition={"left"}
                    variant={"outline"}
                    color={"blue"}
                    icon={<UserPlusIcon color={""} size={0} />}
                    onClick={() => handleRestoreContacts()}
                />
            }
            {
                !(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) &&
                !isInBin &&
                <ButtonDropdown
                    options={[
                        {
                            disabled: (
                                (keycloak.hasRealmRole('commercial') && !usersState.findMeResponse?.isAutonomous) ||
                                (keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator'))
                            ),
                            label: 'Cestina tutti',
                            onClick: () => setShowSoftBulkModal(true)
                        }
                    ]}
                    size={"sm"}
                    iconPosition={"left"}
                    color={"blue"}
                    onClick={() => navigate('/target-list-bins/' + targetList.id + '/companies/' + projectId)}
                    label="Visualizza Ko"
                    icon={<TrashIcon color={""} size={0} />}
                />
            }
            {
                (keycloak.hasRealmRole('admin') || (keycloak.hasRealmRole('commercial') && usersState.findMeResponse?.isAutonomous) || keycloak.hasRealmRole('team_leader')) && contacts.total !== 0 &&
                (downloadCompaniesCsv.loading ?
                    <Spinner /> :
                    <ButtonDropdown
                        disabled={selectedIds.length <= 0}
                        options={[
                            {
                                label: 'Esporta tutti',
                                onClick: () => {
                                    setShowDownloadBulkContactsModal(true)
                                }
                            }
                        ]}
                        size={"sm"}
                        iconPosition={"left"}
                        color={"blue"}
                        onClick={() => downloadCompaniesCsv.handleDownloadConfirm()}
                        label="Esporta"
                        icon={<DownloadIcon color={""} size={0} />}
                    />)
            }
            {
                !(
                    keycloak.hasRealmRole('customer') ||
                    keycloak.hasRealmRole('collaborator') ||
                    keycloak.hasRealmRole('commercial')
                )
                && contacts.total !== 0 &&
                <ButtonDropdown
                    disabled={selectedIds.length <= 0}
                    icon={<TrashIcon color={""} size={0} />}
                    options={[
                        {
                            label: 'Elimina tutti',
                            onClick: () => setDeleteBulkContactsModal(true)
                        }
                    ]}
                    onClick={() => setShowPopup(true)}
                    label="Elimina"
                    size={"sm"}
                    iconPosition={"left"}
                    color={"blue"}
                />
            }
            {
                !(keycloak.hasRealmRole('commercial') && !usersState.findMeResponse?.isAutonomous) &&
                !(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) &&
                <Button
                    size={"sm"}
                    iconPosition={"left"}
                    variant={"solid"}
                    color={"blue"}
                    icon={<PlusIcon color={""} size={0} />}
                    label={
                        "Aggiungi aziende"
                    }
                    onClick={() =>
                        dispatch(setTargetListPage(keycloak.hasRealmRole('commercial') ? TargetListPageEnum.DB : TargetListPageEnum.ADD))
                    }
                />
            }
        </div>
    )

    // return (
    //     <div className="flex items-center gap-2">
    //         {
    //             !(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) && contacts.total !== 0 &&
    //             <Button
    //                 size={"sm"}
    //                 iconPosition={"left"}
    //                 variant={"outline"}
    //                 color={"blue"}
    //                 onClick={() => {
    //                     dispatch(bulkCSVCompanies(filters))
    //                 }}
    //                 label="Esporta tutti"
    //                 icon={<DownloadIcon color={""} size={0} />}
    //             />
    //         }
    //         {selectedIds.length > 0 && (
    //             <>
    //                 {window.location.pathname.includes('target-list-bins') &&
    //                     !(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) &&
    //                     <Button
    //                         size={"sm"}
    //                         iconPosition={"only"}
    //                         variant={"outline"}
    //                         color={"blue"}
    //                         icon={<UserPlusIcon color={""} size={0} />}
    //                         onClick={() => dispatch(restoreContacts(selectedIds.map(id => id.contactId)))}
    //                     />}
    //                 {
    //                     !(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) &&
    //                     <Button
    //                         size={"sm"}
    //                         label="Esporta"
    //                         iconPosition={"left"}
    //                         variant={"outline"}
    //                         color={"blue"}
    //                         icon={<DownloadIcon color={""} size={0} />}
    //                         onClick={() => dispatch(getCSVCompanies(selectedIds.map(id => id.companyId)))}
    //                     />}
    //                 {
    //                     !(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) &&
    //                     <Button
    //                         size={"sm"}
    //                         iconPosition={"only"}
    //                         variant={"outline"}
    //                         color={"blue"}
    //                         icon={<TrashIcon color={""} size={0} />}
    //                         onClick={() => setShowPopup(true)}
    //                     />}
    //             </>
    //         )}
    //     </div>
    // )
}